import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { ChangePasswordDialog } from "@/components/change-password-dialog";
import {
  LayoutDashboard,
  ShieldCheck,
  Boxes,
  Wand2,
  FolderGit2,
  Users2,
  LogOut,
  Plug,
  Wrench,
  KeyRound,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
  Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/app/rules", label: "Governance", icon: ShieldCheck, testid: "nav-rules" },
  { to: "/app/catalog", label: "Catalog", icon: Boxes, testid: "nav-catalog" },
  { to: "/app/generate", label: "Generator", icon: Wand2, testid: "nav-generate" },
  { to: "/app/projects", label: "Projects", icon: FolderGit2, testid: "nav-projects" },
  { to: "/app/iac", label: "IaC", icon: Wrench, testid: "nav-iac" },
  { to: "/app/connectors", label: "Connectors", icon: Plug, testid: "nav-connectors" },
  { to: "/app/patrice", label: "Patrice AI", icon: Bot, testid: "nav-patrice" },
];

// UI preference only (menu folded/unfolded) — never store tokens or secrets in localStorage.
const STORAGE_KEY = "toastern_sidebar_collapsed";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav_go = useNavigate();
  const loc = useLocation();

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  const isAdmin = user?.role === "admin";
  const [pwOpen, setPwOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const toggle = () => setCollapsed((c) => { localStorage.setItem(STORAGE_KEY, c ? "0" : "1"); return !c; });

  const linkClass = (isActive, extra = "") =>
    cn(
      "flex items-center gap-3 border-l-4 border-transparent px-3 py-2.5 font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-50",
      collapsed && "justify-center px-0",
      isActive && "border-brand-signal bg-slate-800 font-bold text-slate-50 hover:bg-slate-800",
      extra,
    );

  return (
    <div className={cn("grid min-h-screen items-start bg-slate-50 transition-[grid-template-columns] duration-200", collapsed ? "grid-cols-[4.25rem_1fr]" : "grid-cols-[18.5rem_1fr]")}>
      <aside className="sticky top-0 flex h-screen flex-col overflow-y-auto border-r border-slate-800 bg-slate-900 text-slate-50" data-testid="sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <div className={cn("flex h-16 items-center border-b border-slate-800", collapsed ? "justify-center px-0" : "px-6")}>
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link" title={user?.company || "Toastern"}>
            <div className="grid h-7 w-7 place-items-center bg-brand-signal text-white">
              <span className="font-display text-sm font-bold">T</span>
            </div>
            {!collapsed && (
              <div className="flex flex-col leading-none">
                <span className="font-display text-sm font-bold tracking-tight text-slate-50">Toastern</span>
                <span className="mt-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">
                  {user?.company || "workspace"}
                </span>
              </div>
            )}
          </Link>
        </div>
        <nav className="flex flex-col gap-1 p-3 text-sm">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} title={n.label} className={({ isActive }) => linkClass(isActive)}>
              <n.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {!collapsed && <span>{n.label}</span>}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              title="Admin"
              className={({ isActive }) =>
                cn(
                  "mt-4 flex items-center gap-3 border border-dashed border-slate-700 px-3 py-2.5 font-medium text-slate-300 transition-colors hover:border-slate-500 hover:text-slate-50",
                  collapsed && "justify-center px-0",
                  isActive && "border-slate-50 bg-slate-800 font-bold text-slate-50",
                )
              }
            >
              <Users2 className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {!collapsed && <span>Admin</span>}
            </NavLink>
          )}
        </nav>
        <div className="mt-auto border-t border-slate-800 p-3">
          <div className={cn("flex items-center gap-3 py-3", collapsed ? "justify-center" : "px-2")} data-testid="user-summary" title={user?.email}>
            <div className="grid h-8 w-8 shrink-0 place-items-center bg-slate-50 text-xs font-bold text-slate-900">
              {initial}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-50">{user?.name || user?.email}</div>
                <div className="truncate font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">{user?.role}</div>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className={cn("mt-1 w-full font-semibold text-slate-300 hover:bg-slate-800 hover:text-slate-50", collapsed ? "justify-center px-0" : "justify-start")}
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-pressed={theme === "dark"}
          >
            {theme === "dark" ? <Sun className={cn("h-4 w-4", !collapsed && "mr-2")} /> : <Moon className={cn("h-4 w-4", !collapsed && "mr-2")} />}
            {!collapsed && (theme === "dark" ? "Light mode" : "Dark mode")}
          </Button>
          <Button
            variant="ghost"
            title="Change password"
            className={cn("mt-1 w-full font-semibold text-slate-300 hover:bg-slate-800 hover:text-slate-50", collapsed ? "justify-center px-0" : "justify-start")}
            onClick={() => setPwOpen(true)}
            data-testid="change-password-button"
          >
            <KeyRound className={cn("h-4 w-4", !collapsed && "mr-2")} /> {!collapsed && "Change password"}
          </Button>
          <Button
            variant="ghost"
            title="Sign out"
            className={cn("w-full font-semibold text-slate-300 hover:bg-slate-800 hover:text-slate-50", collapsed ? "justify-center px-0" : "justify-start")}
            onClick={async () => {
              await logout();
              nav_go("/");
            }}
            data-testid="logout-button"
          >
            <LogOut className={cn("h-4 w-4", !collapsed && "mr-2")} /> {!collapsed && "Sign out"}
          </Button>
          <Button
            variant="ghost"
            title={collapsed ? "Expand menu" : "Collapse menu"}
            className={cn("mt-1 w-full border-t border-slate-800 pt-3 font-semibold text-slate-400 hover:bg-slate-800 hover:text-slate-50", collapsed ? "justify-center px-0" : "justify-start")}
            onClick={toggle}
            data-testid="sidebar-toggle"
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="mr-2 h-4 w-4" />}
            {!collapsed && "Collapse menu"}
          </Button>
          <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        </div>
      </aside>
      <main key={loc.pathname} className="min-h-screen min-w-0 bg-dots-soft fade-up">
        <Outlet />
      </main>
    </div>
  );
}
